import { useEffect, useState } from "react";
import { Building2, ClipboardList, ShieldCheck, XCircle, Ban, ArrowUpRight } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABELS = {
  DRAFT: "Draft", PENDING_VERIFICATION: "Pending", VERIFIED: "Verified",
  REJECTED: "Rejected", SUSPENDED: "Suspended",
};

export default function AdminDashboard({ onOpenCompany }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    client.get("/admin/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((err) => setError(extractError(err, "Unable to load dashboard.")));
  }, []);

  if (error) return <div className="form-error">{error}</div>;
  if (!stats) return <div className="loading-block">Loading dashboard…</div>;
  const counts = stats.counts || {};

  const summary = [
    { key: "total", label: "TOTAL COMPANIES", icon: Building2, value: stats.total, accent: true, testid: "stat-total" },
    { key: "PENDING_VERIFICATION", label: "PENDING VERIFICATION", icon: ClipboardList, value: counts.PENDING_VERIFICATION || 0, testid: "stat-pending" },
    { key: "VERIFIED", label: "VERIFIED", icon: ShieldCheck, value: counts.VERIFIED || 0, testid: "stat-verified" },
    { key: "REJECTED", label: "REJECTED", icon: XCircle, value: counts.REJECTED || 0, testid: "stat-rejected" },
    { key: "SUSPENDED", label: "SUSPENDED", icon: Ban, value: counts.SUSPENDED || 0, testid: "stat-suspended" },
  ];

  const CompanyRow = ({ c, testid }) => (
    <li data-testid={testid}>
      <div>
        <strong>{c.legal_name}</strong>
        <small className="muted">{c.city_name || "—"}{c.state_name ? `, ${c.state_name}` : ""}</small>
      </div>
      <span className={`status-chip status-${c.status.toLowerCase()}`}>{STATUS_LABELS[c.status]}</span>
      <button type="button" className="link-button" onClick={() => onOpenCompany?.(c.id)} data-testid={`dashboard-review-${c.id}`}>
        <ArrowUpRight size={14} />
      </button>
    </li>
  );

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <section className="stats-grid" data-testid="stats-grid">
        {summary.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.key} className={`signal-card ${s.accent ? "accent" : ""}`} data-testid={s.testid}>
              <div className="card-icon"><Icon size={19} /></div>
              <p className="card-label">{s.label}</p>
              <h3>{s.value}</h3>
            </article>
          );
        })}
      </section>

      <section className="dashboard-columns">
        <div className="dashboard-panel" data-testid="panel-recent-registrations">
          <div className="section-heading"><div><p className="eyebrow">PENDING VERIFICATION</p><h3>Recent registrations</h3></div></div>
          <ul className="mini-list">
            {stats.recent_registrations.length === 0 && <li className="muted small">No pending registrations right now.</li>}
            {stats.recent_registrations.map((c) => <CompanyRow key={c.id} c={c} testid={`recent-registration-${c.id}`} />)}
          </ul>
        </div>
        <div className="dashboard-panel" data-testid="panel-recently-verified">
          <div className="section-heading"><div><p className="eyebrow">RECENTLY VERIFIED</p><h3>Approved companies</h3></div></div>
          <ul className="mini-list">
            {stats.recently_verified.length === 0 && <li className="muted small">No verified companies yet.</li>}
            {stats.recently_verified.map((c) => <CompanyRow key={c.id} c={c} testid={`recently-verified-${c.id}`} />)}
          </ul>
        </div>
        <div className="dashboard-panel" data-testid="panel-recently-rejected">
          <div className="section-heading"><div><p className="eyebrow">RECENTLY REJECTED</p><h3>Companies returned for changes</h3></div></div>
          <ul className="mini-list">
            {stats.recently_rejected.length === 0 && <li className="muted small">No rejections in the recent history.</li>}
            {stats.recently_rejected.map((c) => <CompanyRow key={c.id} c={c} testid={`recently-rejected-${c.id}`} />)}
          </ul>
        </div>
      </section>
    </div>
  );
}
